/**
 * Jotai atoms for inventory feature state management
 * Manages search, sort, and pagination state
 *
 * Note: Filter atoms are now in ./filters/ directory
 */

import { atom } from 'jotai';

import { InventoryItemCondition } from '@ekhodealer/ekho-common';

import type { SortDirection } from '@ekhodealer/ekho-common/rev';

import atomWithDebounce from '@common/atoms/atomWithDebounce';

// ============================================================================
// SEARCH
// ============================================================================

// Search value atoms with debouncing
const searchDebounceAtoms = atomWithDebounce('', 500);
export const debouncedSearchValueAtom = searchDebounceAtoms.debouncedValueAtom;
export const searchValueAtom = searchDebounceAtoms.currentValueAtom;

export enum InventoryTab {
	VEHICLE_DETAILS = 'vehicle-details',
	ACCESSORIES = 'accessories',
}

// ============================================================================
// SORTING
// ============================================================================

export const sortConfigAtom = atom<
	Array<{
		column: string;
		direction: SortDirection;
	}>
>([]);

// Action atom to handle sort changes
export const updateSortAtom = atom(
	null,
	(_get, set, payload: { columnId: string; direction: SortDirection }) => {
		const { columnId, direction } = payload;
		if (direction === 'none') {
			set(sortConfigAtom, []);
		} else {
			set(sortConfigAtom, [{ column: columnId, direction }]);
		}
	},
);

// ============================================================================
// PAGINATION
// ============================================================================

export const currentPageAtom = atom<number>(1);
export const itemsPerPageAtom = atom<number>(50);

/**
 * Atom to store the current list of inventory item IDs on the current page
 * Used for prev/next navigation in the inventory drawer
 */
export const currentInventoryListAtom = atom<string[]>([]);

// Action atom to handle search value changes (resets page)
export const updateSearchValueAtom = atom(
	(get) => get(searchValueAtom),
	(_get, set, newValue: string) => {
		set(debouncedSearchValueAtom, newValue);
		// Reset to first page when searching
		set(currentPageAtom, 1);
	},
);

// ============================================================================
// FILTER OPTION TYPES
// ============================================================================

export type VehicleMatchOption = 'in-progress' | 'complete';
export type OnlineSalesOption = 'enabled' | 'lead-gen';
export type PublishStatusOption = 'published' | 'draft' | 'hidden';
export type SyncStatusOption = 'synced' | 'needs-review' | 'fix-required';

// Dynamic filter types (values come from inventory data)
export type MakeFilterValue = string;
export type ModelFilterValue = string;
export type ColorFilterValue = string;
export type VehicleCategoryFilterValue = string;
export type ConditionFilterValue = InventoryItemCondition;
export type ImportJobIdFilterValue = string | null;

// ============================================================================
// AGGREGATE FILTER STATE
// ============================================================================

export interface InventoryFiltersState {
	vehicleMatch: VehicleMatchOption[];
	onlineSales: OnlineSalesOption[];
	publishStatus: PublishStatusOption[];
	make: MakeFilterValue[];
	model: ModelFilterValue[];
	color: ColorFilterValue[];
	vehicleCategory: VehicleCategoryFilterValue[];
	newUsed: ConditionFilterValue[];
	importJobId: ImportJobIdFilterValue;
}

// ============================================================================
// FILTER LABELS (for display)
// ============================================================================

export const VEHICLE_MATCH_LABELS: Record<VehicleMatchOption, string> = {
	'in-progress': 'In progress',
	complete: 'Complete',
};

export const ONLINE_SALES_LABELS: Record<OnlineSalesOption, string> = {
	'enabled': 'Enabled',
	'lead-gen': 'Lead gen',
};

export const PUBLISH_STATUS_LABELS: Record<PublishStatusOption, string> = {
	published: 'Published',
	draft: 'Draft',
	hidden: 'Hidden',
};

// ============================================================================
// APPLIED FILTER ITEM (for chips display)
// ============================================================================

export interface AppliedFilterItem {
	id: string;
	label: string;
	value: string;
	clearFunction: () => void;
}

// ============================================================================
// FILTER ATOMS
// ============================================================================

/**
 * Atom to track vehicle match filter selections
 */
export const vehicleMatchFilterAtom = atom<VehicleMatchOption[]>([]);

/**
 * Atom to track online sales filter selections
 */
export const onlineSalesFilterAtom = atom<OnlineSalesOption[]>([]);

/**
 * Atom to track publish status filter selections
 */
export const publishStatusFilterAtom = atom<PublishStatusOption[]>([]);

/**
 * Atom to track make filter selections (dynamic values from inventory)
 */
export const makeFilterAtom = atom<MakeFilterValue[]>([]);

/**
 * Atom to track model filter selections (dynamic values from inventory)
 */
export const modelFilterAtom = atom<ModelFilterValue[]>([]);

/**
 * Atom to track color filter selections (dynamic values from inventory)
 */
export const colorFilterAtom = atom<ColorFilterValue[]>([]);

/**
 * Atom to track vehicle category filter selections (dynamic values from inventory)
 */
export const vehicleCategoryFilterAtom = atom<VehicleCategoryFilterValue[]>([]);

/**
 * Atom to track condition filter selections
 */
export const conditionFilterAtom = atom<ConditionFilterValue[]>([]);

/**
 * Atom to track sync status filter selections
 */
export const syncStatusFilterAtom = atom<SyncStatusOption[]>([]);

/**
 * Atom to track import job ID filter selection
 */
export const importJobIdFilterAtom = atom<ImportJobIdFilterValue>(null);

// ============================================================================
// RESET ACTION
// ============================================================================

/**
 * Action atom to reset all filter atoms to their default values
 */
export const resetAllFiltersAtom = atom(null, (_get, set) => {
	set(vehicleMatchFilterAtom, []);
	set(onlineSalesFilterAtom, []);
	set(publishStatusFilterAtom, []);
	set(makeFilterAtom, []);
	set(modelFilterAtom, []);
	set(colorFilterAtom, []);
	set(vehicleCategoryFilterAtom, []);
	set(conditionFilterAtom, []);
	set(importJobIdFilterAtom, null);
});

/**
 * Derived atom that aggregates all filter values into a single state object
 */
export const inventoryFiltersAtom = atom<InventoryFiltersState>((get) => ({
	vehicleMatch: get(vehicleMatchFilterAtom),
	onlineSales: get(onlineSalesFilterAtom),
	publishStatus: get(publishStatusFilterAtom),
	make: get(makeFilterAtom),
	model: get(modelFilterAtom),
	color: get(colorFilterAtom),
	vehicleCategory: get(vehicleCategoryFilterAtom),
	newUsed: get(conditionFilterAtom),
	importJobId: get(importJobIdFilterAtom),
}));

// ============================================================================
// API PARAMETERS
// ============================================================================

/**
 * Derived atom for API parameters - includes search, pagination, and filters
 * Returns a structured object matching GetInventoryParams interface
 */
export const inventoryParamsAtom = atom((get) => {
	const searchValue = get(debouncedSearchValueAtom);
	const currentPage = get(currentPageAtom);
	const itemsPerPage = get(itemsPerPageAtom);
	const filters = get(inventoryFiltersAtom);

	return {
		limit: itemsPerPage,
		page: currentPage - 1, // convert the 1-based ui page to 0-based api page for algolia
		query: searchValue.trim() || undefined,
		filters,
		facets: ['make', 'model', 'vehicleCategory', 'newUsed', 'listing.status', 'matching.status', 'isMarkedForOnlineSale'], // Request facets for filter counts
	};
});

/**
 * Derived atom for base API parameters (without user-selected filters)
 * Used for getting facet counts that show total available options
 * Only includes search query to provide context, but excludes all user filters
 * so counts remain constant regardless of filter selections
 * 
 * Note: Includes empty filters object to trigger transformation logic that
 * excludes archived items (listing.status filter applied in useGetInventory.ts)
 */
export const inventoryBaseParamsAtom = atom((get) => {
	const searchValue = get(debouncedSearchValueAtom);

	return {
		limit: 0, // We only need facets, not data
		page: 0,
		query: searchValue.trim() || undefined,
		filters: {}, // Empty filters object triggers archived exclusion in transformation
		facets: ['make', 'model', 'vehicleCategory', 'newUsed', 'listing.status', 'matching.status', 'isMarkedForOnlineSale'] // Request facets for filter counts
	};
});

/**
 * Derived atom for matched facet parameters
 * Used for getting facet counts for make, model, and vehicleCategory
 * Filters by matching.status = MATCHED (complete) to show only matched vehicles
 * Also excludes archived items via transformation logic
 */
export const inventoryMatchedFacetParamsAtom = atom((get) => {
	const searchValue = get(debouncedSearchValueAtom);

	return {
		limit: 0, // We only need facets, not data
		page: 0,
		query: searchValue.trim() || undefined,
		filters: {
			vehicleMatch: ['complete'] as VehicleMatchOption[], // Maps to matching.status = MATCHED in transformation
		},
		facets: ['make', 'model', 'vehicleCategory'], // Only request facets for matched filters
	};
});

/**
 * Action atoms for updating individual filter values
 */
export const updateVehicleMatchFilterAtom = atom(
	(get) => get(vehicleMatchFilterAtom),
	(_get, set, newValue: VehicleMatchOption[]) => {
		set(vehicleMatchFilterAtom, newValue);
		set(currentPageAtom, 1);
	},
);

export const updateOnlineSalesFilterAtom = atom(
	(get) => get(onlineSalesFilterAtom),
	(_get, set, newValue: OnlineSalesOption[]) => {
		set(onlineSalesFilterAtom, newValue);
		set(currentPageAtom, 1);
	},
);

export const updatePublishStatusFilterAtom = atom(
	(get) => get(publishStatusFilterAtom),
	(_get, set, newValue: PublishStatusOption[]) => {
		set(publishStatusFilterAtom, newValue);
		set(currentPageAtom, 1);
	},
);

export const updateMakeFilterAtom = atom(
	(get) => get(makeFilterAtom),
	(_get, set, newValue: MakeFilterValue[]) => {
		set(makeFilterAtom, newValue);
		set(currentPageAtom, 1);
	},
);

export const updateModelFilterAtom = atom(
	(get) => get(modelFilterAtom),
	(_get, set, newValue: ModelFilterValue[]) => {
		set(modelFilterAtom, newValue);
		set(currentPageAtom, 1);
	},
);

export const updateColorFilterAtom = atom(
	(get) => get(colorFilterAtom),
	(_get, set, newValue: ColorFilterValue[]) => {
		set(colorFilterAtom, newValue);
		set(currentPageAtom, 1);
	},
);

export const updateVehicleCategoryFilterAtom = atom(
	(get) => get(vehicleCategoryFilterAtom),
	(_get, set, newValue: VehicleCategoryFilterValue[]) => {
		set(vehicleCategoryFilterAtom, newValue);
		set(currentPageAtom, 1);
	},
);

export const updateConditionFilterAtom = atom(
	(get) => get(conditionFilterAtom),
	(_get, set, newValue: ConditionFilterValue[]) => {
		set(conditionFilterAtom, newValue);
		set(currentPageAtom, 1);
	},
);

export const updateImportJobIdFilterAtom = atom(
	(get) => get(importJobIdFilterAtom),
	(_get, set, newValue: ImportJobIdFilterValue) => {
		set(importJobIdFilterAtom, newValue);
		set(currentPageAtom, 1);
	},
);

/**
 * Derived atom that creates a list of applied filters for display
 * Creates individual chips for each selected value (like orders)
 */
export const allAppliedFiltersAtom = atom<AppliedFilterItem[]>((get) => {
	const appliedFilters: AppliedFilterItem[] = [];

	const vehicleMatch = get(vehicleMatchFilterAtom);
	const onlineSalesFilter = get(onlineSalesFilterAtom);
	const publishStatus = get(publishStatusFilterAtom);
	const make = get(makeFilterAtom);
	const model = get(modelFilterAtom);
	const color = get(colorFilterAtom);
	const vehicleCategory = get(vehicleCategoryFilterAtom);
	const condition = get(conditionFilterAtom);
	const importJobId = get(importJobIdFilterAtom);

	// Add vehicle match filters - create individual chip for each selection
	if (vehicleMatch.length > 0) {
		vehicleMatch.forEach((value) => {
			appliedFilters.push({
				id: `vehicle-match-${value}`,
				label: 'Vehicle match',
				value: VEHICLE_MATCH_LABELS[value],
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add online sales filters - create individual chip for each selection
	if (onlineSalesFilter.length > 0) {
		onlineSalesFilter.forEach((value) => {
			appliedFilters.push({
				id: `online-sales-${value}`,
				label: 'Online sales',
				value: ONLINE_SALES_LABELS[value],
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add publish status filters - create individual chip for each selection
	if (publishStatus.length > 0) {
		publishStatus.forEach((value) => {
			appliedFilters.push({
				id: `publish-status-${value}`,
				label: 'Publish status',
				value: PUBLISH_STATUS_LABELS[value],
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add make filters - create individual chip for each selection
	if (make.length > 0) {
		make.forEach((value) => {
			appliedFilters.push({
				id: `make-${value}`,
				label: 'Make',
				value,
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add model filters - create individual chip for each selection
	if (model.length > 0) {
		model.forEach((value) => {
			appliedFilters.push({
				id: `model-${value}`,
				label: 'Model',
				value,
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add color filters - create individual chip for each selection
	if (color.length > 0) {
		color.forEach((value) => {
			appliedFilters.push({
				id: `color-${value}`,
				label: 'Color',
				value,
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add vehicle category filters - create individual chip for each selection
	if (vehicleCategory.length > 0) {
		vehicleCategory.forEach((value) => {
			appliedFilters.push({
				id: `vehicleCategory-${value}`,
				label: 'Category',
				value,
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add condition filters - create individual chip for each selection
	if (condition.length > 0) {
		const conditionLabels: Record<InventoryItemCondition, string> = {
			[InventoryItemCondition.NEW]: 'New',
			[InventoryItemCondition.USED]: 'Used',
			[InventoryItemCondition.CONSIGNED]: 'Consigned',
			[InventoryItemCondition.UNDEFINED]: 'Undefined',
		};
		condition.forEach((value) => {
			appliedFilters.push({
				id: `condition-${value}`,
				label: 'Condition',
				value: conditionLabels[value],
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add import job filter (single selection)
	if (importJobId) {
		appliedFilters.push({
			id: 'importJobId',
			label: 'Import Job',
			value: importJobId,
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	return appliedFilters;
});

/**
 * Derived atom that returns the count of applied filter categories
 */
export const appliedFiltersCountAtom = atom((get) => {
	return get(allAppliedFiltersAtom).length;
});

/**
 * Derived atom that checks if any filters are applied
 */
export const hasAppliedFiltersAtom = atom((get) => {
	return get(appliedFiltersCountAtom) > 0;
});
